import React, { useEffect, useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Send, Loader2, Trash2, ScrollText, Feather, BookOpenText, Crown } from "lucide-react";
import { GRAMMARIEN } from "@/constants/testIds";
import { chatGrammarien, chatGrammarienUsage, chatGrammarienClear } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { useAccount } from "@/lib/account";
import { PLAYLISTS, ACCENTS } from "@/lib/tracks";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "astrovoyance.grammarien_session";

const LANGS = [
  { id: "gaeilge", label: "Gaélique irlandais", en: "Irish Gaelic", note: "VSO · mutations initiales · alphabet latin",
    intro: {
      fr: "Structure VSO (Verbe-Sujet-Objet), mutations séimhiú/urú, article an/na. Ex : *Itheann Seán úll* = mange Seán une-pomme.",
      en: "VSO structure (Verb-Subject-Object), séimhiú/urú mutations, article an/na. Ex: *Itheann Seán úll* = eats Seán an-apple.",
    } },
  { id: "latin", label: "Latin classique", en: "Classical Latin", note: "SOV dominant · 6 cas · déclinaisons",
    intro: {
      fr: "Six cas (nom/voc/acc/gén/dat/abl), déclinaisons, ordre libre mais SOV dominant. Ex : *Puella rosam amat* = la-fille rose-COD aime.",
      en: "Six cases (nom/voc/acc/gen/dat/abl), declensions, free word order but SOV-dominant. Ex: *Puella rosam amat* = the-girl rose-ACC loves.",
    } },
  { id: "grec", label: "Grec ancien", en: "Ancient Greek", note: "alphabet grec + translittération · aspects verbaux",
    intro: {
      fr: "Alphabet grec, translittération systématique, aspects présent/aoriste/parfait, accents (aigu, grave, circonflexe).",
      en: "Greek alphabet, systematic transliteration, present/aorist/perfect aspects, accents (acute, grave, circumflex).",
    } },
  { id: "hawaien", label: "Hawaïen (ʻōlelo Hawaiʻi)", en: "Hawaiian (ʻōlelo Hawaiʻi)", note: "VSO · particules aspectuelles · ʻokina & kahakō",
    intro: {
      fr: "Structure VSO comme le gaélique. Particules ua (accompli), e...ana (progressif), ke...nei (présent). Respect des ʻokina (') et kahakō (macron).",
      en: "VSO structure like Gaelic. Particles ua (perfective), e...ana (progressive), ke...nei (present). Respect ʻokina (') and kahakō (macron).",
    } },
  { id: "francais-vso", label: "Français en VSO", en: "French in VSO", note: "signature du Grammarien — inversion celtique",
    intro: {
      fr: "Signature d'Iona : reformuler le français normal (SVO) en VSO celtique. Ex : « Jean mange une pomme » → « Mange Jean une pomme. » L'action passe AVANT l'acteur.",
      en: "Iona's signature: reformulate normal French (SVO) in Celtic VSO. Ex: 'Jean mange une pomme' → 'Mange Jean une pomme.' Action comes BEFORE the actor.",
    } },
];

const SUGGESTIONS = {
  fr: [
    "Comment dire « la lune brille » en gaélique irlandais ?",
    "Convertis « J'aime la mer » en français VSO",
    "Décline le mot rosa en latin",
    "Comment saluer en hawaïen le matin ?",
    "Explique-moi l'aoriste grec en 3 exemples",
    "Fais-moi un mini-exercice de mutation initiale en gaélique",
  ],
  en: [
    "How do you say 'the moon shines' in Irish Gaelic?",
    "Convert 'I love the sea' into French VSO",
    "Decline the word rosa in Latin",
    "How to greet in Hawaiian in the morning?",
    "Explain the Greek aorist in 3 examples",
    "Give me a Gaelic initial-mutation mini-exercise",
  ],
};

export default function Grammarien() {
  const { lang, t, setLang } = useLang();
  const { email, status } = useAccount() || {};
  const [selectedLang, setSelectedLang] = useState("gaeilge");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);

  useEffect(() => {
    chatGrammarienUsage(email).then(setUsage).catch(() => {});
  }, [email, status?.premium_active, messages.length]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    const selectedMeta = LANGS.find((l) => l.id === selectedLang);
    const prefix = lang === "fr"
      ? `Langue étudiée : ${selectedMeta?.label}. Question : `
      : `Language studied: ${selectedMeta?.en}. Question: `;
    setMessages((m) => [...m, { sender: "user", message: trimmed, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatGrammarien(prefix + trimmed, sessionId, email, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "daily_grammarien_limit_reached") {
        toast.error(lang === "fr"
          ? `Le Grammarien a offert ${usage.limit} leçons aujourd'hui. Passe Premium pour continuer.`
          : `The Grammarian offered ${usage.limit} lessons today. Go Premium to continue.`);
        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error(lang === "fr" ? "Le Grammarien tourne les pages. Réessaie." : "The Grammarian is turning pages. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    try { await chatGrammarienClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(lang === "fr" ? "Notes effacées." : "Notes wiped.");
  };

  const currentLang = LANGS.find((l) => l.id === selectedLang) || LANGS[0];
  const isPremium = usage.premium_active;
  const limitReached = !isPremium && (usage.remaining ?? 20) <= 0;
  const suggestions = lang === "fr" ? SUGGESTIONS.fr : SUGGESTIONS.en;

  return (
    <div data-testid={GRAMMARIEN.page} className="gram-root min-h-screen relative">
      <div className="gram-bg" aria-hidden="true" />
      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#e6d9a2" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(90,70,45,0.35)", border: "1px solid rgba(220,200,150,0.28)" }}>
              <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "fr" ? "#e6d9a2" : "transparent", color: lang === "fr" ? "#1a1108" : "#d5c8a5", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "en" ? "#e6d9a2" : "transparent", color: lang === "en" ? "#1a1108" : "#d5c8a5", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <span data-testid={GRAMMARIEN.usage}
                  className="inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-2 rounded-full"
                  style={{ background: isPremium ? "rgba(212,175,55,0.18)" : "rgba(60,45,25,0.55)",
                           border: "1px solid rgba(220,200,150,0.28)",
                           color: isPremium ? "#f0d97a" : "#d5c8a5" }}>
              {isPremium
                ? <><Crown size={11} /> {lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free"}</>
                : <>{usage.used_today}/{usage.limit} · {lang === "fr" ? "leçons aujourd'hui" : "lessons today"}</>}
            </span>
            <MusicPicker playlist={PLAYLISTS.grammarien} accent={ACCENTS.grammarien} testId="grammarien-music" persona="Grammarien" />
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="overline mb-4" style={{ color: "#e6d9a2" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Le Grammarien d'Iona · langues sacrées · gratuit illimité" : "The Grammarian of Iona · sacred languages · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f4ead0" }}>
          {lang === "fr" ? (
            <>Apprends les langues <span className="italic" style={{ color: "#e6d9a2" }}>sacrées</span> du monde.</>
          ) : (
            <>Learn the world's <span className="italic" style={{ color: "#e6d9a2" }}>sacred</span> languages.</>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#d5c8a5" }}>
          {lang === "fr"
            ? "Gaélique irlandais (alphabet latin), latin classique, grec ancien, hawaïen — et la spécialité d'Iona : le français en structure VSO celtique. Grammaire claire, exemples, exercices. Gratuit et illimité."
            : "Irish Gaelic (Latin alphabet), Classical Latin, Ancient Greek, Hawaiian — and Iona's signature: French in Celtic VSO structure. Clear grammar, examples, exercises. Free and unlimited."}
        </p>
      </section>

      {/* Language cards */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {LANGS.map((l) => (
            <button key={l.id} type="button"
                    data-testid={GRAMMARIEN.langChip(l.id)}
                    onClick={() => setSelectedLang(l.id)}
                    className="gram-lang-card"
                    style={{
                      background: selectedLang === l.id ? "rgba(230,217,162,0.16)" : "rgba(30,22,14,0.6)",
                      borderColor: selectedLang === l.id ? "rgba(230,217,162,0.65)" : "rgba(220,200,150,0.2)",
                    }}>
              <BookOpenText size={16} style={{ color: "#e6d9a2" }} />
              <div className="text-sm font-serif" style={{ color: "#f4ead0" }}>{lang === "fr" ? l.label : l.en}</div>
              <div className="text-[10px] font-mono" style={{ color: "#a89676" }}>{l.note}</div>
            </button>
          ))}
        </div>

        {/* Intro card */}
        <div className="mt-5 gram-intro">
          <div className="flex items-center gap-2 mb-2">
            <ScrollText size={16} style={{ color: "#e6d9a2" }} />
            <div className="overline" style={{ color: "#e6d9a2" }}>
              {lang === "fr" ? currentLang.label : currentLang.en}
            </div>
          </div>
          <p className="text-sm" style={{ color: "#d5c8a5" }}>
            {lang === "fr" ? currentLang.intro.fr : currentLang.intro.en}
          </p>
        </div>
      </section>

      {/* Chat */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-24">
        {limitReached ? (
          <div className="gram-upgrade">
            <Crown size={16} style={{ color: "#f0d97a" }} />
            <span className="text-sm">
              {lang === "fr"
                ? <>Les {usage.limit} leçons gratuites sont épuisées. <a href="/#pricing" className="underline" style={{ color: "#f0d97a" }}>Passe Premium à 15 $/mois</a> pour un accès illimité au Grammarien.</>
                : <>The {usage.limit} free lessons are exhausted. <a href="/#pricing" className="underline" style={{ color: "#f0d97a" }}>Go Premium at $15/mo</a> for unlimited access.</>}
            </span>
          </div>
        ) : (
          <div className="gram-chat" data-testid={GRAMMARIEN.chatBox}>
            <div ref={boxRef} className="gram-messages">
              {messages.length === 0 && (
                <div className="gram-empty">
                  <Feather size={22} style={{ color: "#e6d9a2" }} />
                  <p className="mt-3 font-serif text-xl" style={{ color: "#f4ead0" }}>
                    {lang === "fr" ? "Le Grammarien d'Iona ouvre son manuscrit. Que veux-tu apprendre ?" : "The Grammarian of Iona opens his manuscript. What shall we learn?"}
                  </p>
                  <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "#a89676" }}>
                    {lang === "fr" ? "Suggestions :" : "Suggestions:"}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {suggestions.map((s, i) => (
                      <button key={i} type="button"
                              data-testid={GRAMMARIEN.suggestion(i)}
                              onClick={() => send(s)}
                              className="gram-chip">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`gram-msg ${m.sender === "user" ? "user" : "advisor"}`}>
                  <div className="gram-msg-inner">
                    {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                    {m.sender === "advisor" && (
                      <div className="mt-2">
                        <VoiceButton text={m.message} lang={lang} accent="#e6d9a2" testId={`grammarien-voice-${i}`} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="gram-msg advisor">
                  <div className="gram-msg-inner inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" />
                    <span className="text-sm" style={{ color: "#d5c8a5" }}>{lang === "fr" ? "Le Grammarien vérifie son Ó Dónaill…" : "Grammarian checking his Ó Dónaill…"}</span>
                  </div>
                </div>
              )}
            </div>

            <form className="gram-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
              <textarea data-testid={GRAMMARIEN.input}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={lang === "fr" ? `Pose une question sur ${currentLang.label}…` : `Ask about ${currentLang.en}…`}
                        rows={2}
                        className="gram-textarea"
                        disabled={loading} />
              <div className="flex items-center gap-2">
                <button type="button" onClick={wipe} data-testid={GRAMMARIEN.clear} className="gram-btn-ghost">
                  <Trash2 size={14} />
                </button>
                <button type="submit" disabled={loading || !input.trim()} data-testid={GRAMMARIEN.send} className="gram-btn-primary">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {lang === "fr" ? "Envoyer" : "Send"}
                </button>
              </div>
            </form>
            <ChatDisclaimer testId="grammarien-ai-disclaimer" />
          </div>
        )}
      </section>

      <style>{`
        .gram-root { background: radial-gradient(ellipse at top, #1e150a 0%, #120b04 55%, #060402 100%); color: #f4ead0; overflow-x: hidden; }
        .gram-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 20% 25%, rgba(230,217,162,0.14) 0%, transparent 45%),
            radial-gradient(circle at 80% 75%, rgba(160,120,80,0.12) 0%, transparent 42%),
            repeating-linear-gradient(0deg, rgba(255,240,200,0.02) 0px, rgba(255,240,200,0.02) 1px, transparent 1px, transparent 22px),
            linear-gradient(180deg, #1a120a 0%, #05030a 100%); }
        .gram-lang-card { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 14px 16px; border-radius: 14px; border: 1px solid; text-align: left; transition: all 220ms ease; }
        .gram-lang-card:hover { transform: translateY(-2px); }
        .gram-intro { padding: 16px 20px; border-radius: 16px; background: rgba(30,22,14,0.6); border: 1px solid rgba(220,200,150,0.22); }
        .gram-chat { background: linear-gradient(180deg, rgba(24,17,10,0.9), rgba(10,6,3,0.94)); border: 1px solid rgba(220,200,150,0.22); border-radius: 22px; padding: 20px; box-shadow: 0 24px 60px -30px rgba(0,0,0,0.75); }
        .gram-messages { min-height: 380px; max-height: 62vh; overflow-y: auto; padding: 12px 4px 20px; margin-bottom: 16px; }
        .gram-empty { text-align: center; padding: 28px 12px; }
        .gram-chip { padding: 8px 14px; border-radius: 999px; background: rgba(60,45,25,0.55); border: 1px solid rgba(220,200,150,0.28); color: #e6d9a2; font-size: 12px; font-family: "IBM Plex Mono", monospace; transition: all 200ms ease; }
        .gram-chip:hover { background: rgba(230,217,162,0.18); border-color: rgba(230,217,162,0.7); color: #f4ead0; }
        .gram-msg { display: flex; margin: 10px 0; }
        .gram-msg.user { justify-content: flex-end; }
        .gram-msg.advisor { justify-content: flex-start; }
        .gram-msg-inner { max-width: 82%; padding: 14px 18px; border-radius: 16px; line-height: 1.65; font-size: 14.5px; }
        .gram-msg.user .gram-msg-inner { background: linear-gradient(135deg, #a67f4b, #6d5030); color: #f8f4e5; border-bottom-right-radius: 4px; }
        .gram-msg.advisor .gram-msg-inner { background: rgba(28,22,14,0.9); color: #f4ead0; border: 1px solid rgba(220,200,150,0.22); border-bottom-left-radius: 4px; }
        .gram-msg-inner p { margin: 0 0 10px; }
        .gram-msg-inner p:last-child { margin-bottom: 0; }
        .gram-msg-inner strong { color: #e6d9a2; }
        .gram-msg-inner em { color: #d5c8a5; }
        .gram-msg-inner h1, .gram-msg-inner h2, .gram-msg-inner h3 { font-family: "Cormorant Garamond", "Georgia", serif; color: #e6d9a2; margin: 6px 0 8px; }
        .gram-msg-inner code { background: rgba(230,217,162,0.14); padding: 1px 6px; border-radius: 4px; color: #f4ead0; font-size: 13.5px; }
        .gram-form { display: flex; gap: 10px; align-items: flex-end; border-top: 1px solid rgba(220,200,150,0.16); padding-top: 14px; }
        .gram-textarea { flex: 1; resize: none; padding: 12px 14px; border-radius: 14px; background: rgba(14,10,4,0.7); border: 1px solid rgba(220,200,150,0.22); color: #f4ead0; font-family: inherit; font-size: 14.5px; outline: none; }
        .gram-textarea:focus { border-color: rgba(230,217,162,0.7); }
        .gram-btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 12px; background: linear-gradient(135deg, #e6d9a2, #b5a25e); color: #1a1108; font-weight: 600; font-size: 13px; font-family: "IBM Plex Mono", monospace; transition: transform 200ms ease, opacity 200ms ease; }
        .gram-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
        .gram-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .gram-btn-ghost { display: inline-flex; align-items: center; padding: 12px 14px; border-radius: 12px; background: rgba(180,40,40,0.15); border: 1px solid rgba(255,120,120,0.3); color: #ffb8a8; }
        .gram-upgrade { display: flex; align-items: center; gap: 10px; padding: 20px 22px; border-radius: 22px; background: rgba(240,217,122,0.1); border: 1px solid rgba(240,217,122,0.4); color: #f4ead0; }
      `}</style>
    </div>
  );
}
